"use client";

import React, { useEffect, useRef, useState } from "react";
import { Activity, ArrowRight, Dumbbell, Hand, HeartPulse, Sparkles, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  id: "srv-1",
  title: "General Consultation",
  description: "Comprehensive health assessments and personalized treatment plans tailored to your specific needs at our Nicosia clinic.",
  iconName: "Stethoscope",
  link: "/TreatmentsPage"
}, {
  id: "srv-2",
  title: "Chiropractic Care",
  description: "Expert spinal adjustments to alleviate pain, improve mobility, and enhance overall nervous system function.",
  iconName: "Activity",
  link: "/ChiropractorInLimassolPage"
}, {
  id: "srv-3",
  title: "Physiotherapy",
  description: "Targeted exercises and manual therapy to restore movement and function after injury, surgery, or illness.",
  iconName: "HeartPulse",
  link: "/TreatmentsPage"
}, {
  id: "srv-4",
  title: "Massage Therapy",
  description: "Therapeutic massage techniques to relieve deep muscle tension, reduce stress, and promote physical relaxation.",
  iconName: "Hand",
  link: "/TreatmentsPage"
}, {
  id: "srv-5",
  title: "Acupuncture",
  description: "Traditional holistic treatments to stimulate natural healing, manage chronic pain, and restore energy balance.",
  iconName: "Sparkles",
  link: "/TreatmentsPage"
}, {
  id: "srv-6",
  title: "Sports Rehabilitation",
  description: "Specialized recovery programs designed to help athletes heal from injuries and return to peak performance safely.",
  iconName: "Dumbbell",
  link: "/TreatmentsPage"
}];
const renderIcon = (name: string, className?: string) => {
  switch (name) {
    case "Stethoscope":
      return <Stethoscope className={className} />;
    case "Activity":
      return <Activity className={className} />;
    case "HeartPulse":
      return <HeartPulse className={className} />;
    case "Hand":
      return <Hand className={className} />;
    case "Sparkles":
      return <Sparkles className={className} />;
    case "Dumbbell":
      return <Dumbbell className={className} />;
    default:
      return <Activity className={className} />;
  }
};
function ServiceCard({
  service,
  index
}: {
  service: typeof services[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const delay = index * 100;
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${delay}ms`
  }} className={`transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
      <Card className="flex h-full flex-col transition-shadow duration-300 hover:shadow-md">
        <CardHeader>
          <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            {renderIcon(service.iconName, "size-6")}
          </div>
          <CardTitle className="text-xl">{service.title}</CardTitle>
        </CardHeader>
        <CardContent className="flex-grow">
          <CardDescription className="text-base leading-relaxed">
            {service.description}
          </CardDescription>
        </CardContent>
        <CardFooter>
          <Button variant="ghost" className="w-full justify-start px-0 hover:bg-transparent hover:text-primary" asChild>
            <Link to={service.link}>
              Learn more <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </div>;
}
export default function ServicesAtThisLocation() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="services-at-this-location" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`mx-auto mb-12 max-w-2xl text-center transition-all duration-700 ease-out md:mb-16 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Services at Nicosia Clinic
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Explore our comprehensive range of specialized treatments and therapies designed to support your health and well-being.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {services.map((service, i) => <ServiceCard key={service.id} index={i} service={service} />)}
        </div>
      </div>
    </section>;
}